if (!customElements.get('laps-product-lightbox')) {
  customElements.define(
    'laps-product-lightbox',
    class LapsProductLightbox extends ModalDialog {
      constructor() {
        super();
        this.track = this.querySelector('.laps-lightbox__track');
        this.slides = Array.from(this.querySelectorAll('.laps-lightbox__slide'));
        this.dots = Array.from(this.querySelectorAll('.laps-lightbox__dot'));
        this.counterCurrent = this.querySelector('.laps-lightbox__counter-current');
        this.prevButton = this.querySelector('.laps-lightbox__arrow--prev');
        this.nextButton = this.querySelector('.laps-lightbox__arrow--next');
        this.currentIndex = 0;

        this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
        this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const index = parseInt(dot.dataset.index, 10);
            if (!Number.isNaN(index)) this.goTo(index);
          });
        });

        this.addEventListener('keydown', (event) => {
          if (event.code === 'ArrowLeft') this.goTo(this.currentIndex - 1);
          if (event.code === 'ArrowRight') this.goTo(this.currentIndex + 1);
        });

        this.bindSwipe();
        this.markZoomableSlides();
        this.bindZoom();
      }

      bindSwipe() {
        if (!this.track) return;
        let startX = 0;
        let startY = 0;
        let deltaX = 0;
        let tracking = false;
        const threshold = 50;

        this.track.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) return;
            tracking = true;
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            deltaX = 0;
          },
          { passive: true }
        );

        this.track.addEventListener(
          'touchmove',
          (event) => {
            if (!tracking) return;
            const dx = event.touches[0].clientX - startX;
            const dy = event.touches[0].clientY - startY;
            if (Math.abs(dy) > Math.abs(dx)) {
              tracking = false;
              return;
            }
            deltaX = dx;
          },
          { passive: true }
        );

        this.track.addEventListener('touchend', () => {
          if (!tracking) return;
          tracking = false;
          if (deltaX > threshold) this.goTo(this.currentIndex - 1);
          else if (deltaX < -threshold) this.goTo(this.currentIndex + 1);
        });
      }

      /* Zoom is desktop-only (no hover on touch; mobile keeps pinch-zoom). */
      isDesktop() {
        return window.matchMedia('(min-width: 750px)').matches;
      }

      /* Tag slides that are plain images. Video / 3D slides keep their own
         poster-click behaviour and never get the zoom cursor or toggle. */
      markZoomableSlides() {
        this.slides.forEach((slide) => {
          const hasRichMedia = slide.querySelector('deferred-media, product-model');
          const img = slide.querySelector('.laps-lightbox__slide-inner img');
          if (img && !hasRichMedia) slide.classList.add('laps-lightbox__slide--zoomable');
        });
      }

      bindZoom() {
        this.slides.forEach((slide) => {
          if (!slide.classList.contains('laps-lightbox__slide--zoomable')) return;
          const img = slide.querySelector('.laps-lightbox__slide-inner img');
          if (!img) return;

          img.addEventListener('click', (event) => {
            if (!this.isDesktop()) return;
            event.preventDefault();
            event.stopPropagation();
            if (slide.classList.contains('is-zoomed')) this.zoomOut(slide, img);
            else this.zoomIn(slide, img, event);
          });

          // While zoomed, follow the cursor so the 2x view can be panned.
          img.addEventListener('mousemove', (event) => {
            if (!this.isDesktop() || !slide.classList.contains('is-zoomed')) return;
            this.setZoomOrigin(img, event);
          });
        });
      }

      setZoomOrigin(img, event) {
        const rect = img.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      }

      zoomIn(slide, img, event) {
        this.setZoomOrigin(img, event);
        slide.classList.add('is-zoomed');
        this.classList.add('is-zoomed');
        img.style.transform = 'scale(2)';
      }

      zoomOut(slide, img) {
        slide.classList.remove('is-zoomed');
        this.classList.remove('is-zoomed');
        img.style.transform = '';
        img.style.transformOrigin = '';
      }

      resetZoom() {
        this.classList.remove('is-zoomed');
        this.slides.forEach((slide) => {
          if (!slide.classList.contains('is-zoomed')) return;
          const img = slide.querySelector('.laps-lightbox__slide-inner img');
          if (img) this.zoomOut(slide, img);
          else slide.classList.remove('is-zoomed');
        });
      }

      goTo(index) {
        if (!this.slides.length) return;
        this.resetZoom();
        const max = this.slides.length - 1;
        const clamped = Math.max(0, Math.min(max, index));
        this.currentIndex = clamped;
        this.track.style.transform = `translateX(-${clamped * 100}%)`;

        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === clamped));
        if (this.counterCurrent) this.counterCurrent.textContent = String(clamped + 1);

        if (this.prevButton) this.prevButton.disabled = clamped === 0;
        if (this.nextButton) this.nextButton.disabled = clamped === max;

        const slide = this.slides[clamped];
        const deferred = slide?.querySelector('deferred-media');
        if (deferred && slide.querySelector('template .js-youtube')) deferred.loadContent?.();
      }

      show(opener) {
        if (!this.slides.length) return;
        super.show(opener);
        const mediaId = opener?.getAttribute('data-media-id');
        const targetIndex = mediaId ? this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId)) : -1;
        this.goTo(targetIndex >= 0 ? targetIndex : 0);
      }

      hide() {
        super.hide();
        this.resetZoom();
      }
    }
  );
}

/* Desktop: clicking anywhere on the product media opens the lightbox at the
   clicked image. Replaces the fixed "+" button (now mobile-only). Mobile keeps
   tap-to-swipe + the fixed button, so this listener bails below 750px.
   Delegated on the document so it fires regardless of what overlays the
   per-thumbnail toggle in the dual-scroll layout. */
document.addEventListener('click', (event) => {
  if (!window.matchMedia('(min-width: 750px)').matches) return;

  const wrapper = event.target.closest('.product__media-wrapper');
  if (!wrapper || !wrapper.closest('.product-main--laps')) return;

  // Let the fixed opener and genuine controls (3D/XR) handle their own clicks.
  if (event.target.closest('.laps-open-lightbox-opener, .product__xr-button, [data-shopify-xr]')) return;

  const container = event.target.closest('.product-media-container');
  if (!container) return;

  // ModalDialog.connectedCallback() relocates the lightbox to <body>, so it is
  // NOT a descendant of the section. The per-image modal-opener's data-modal is
  // also broken (#LapsLightbox- with no section id), so resolve the single
  // lightbox element directly — there is one per product page.
  const lightbox = document.querySelector('laps-product-lightbox');
  if (!lightbox || typeof lightbox.show !== 'function') return;

  // The toggle carries data-media-id, which lightbox.show() uses to pick the slide.
  const opener = container.querySelector('.product__media-toggle') || container;
  event.preventDefault();
  lightbox.show(opener);
});
